import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import AppLoader from '../shared/AppLoader';
import Button from '../shared/Button';
import useAPI from '../utils/api';
import { editorURL } from '../utils/editor';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NewCampaignDialog from './Modals/NewCampaignDialog';
import ConfirmationDialog from './Modals/ConfirmationDialog';

// 4 internal statuses grouped into 3 tabs users actually care about. Delete
// replaces Archive entirely (backend `destroy` is a real soft-delete).
const GROUPS = {
  upcoming: ['draft'],
  active: ['active'],
  stopped: ['paused', 'completed'],
};

const STATUS_LABELS = {
  draft: 'Upcoming',
  active: 'Active',
  paused: 'Stopped',
  completed: 'Stopped',
};

const STATUS_COLORS = {
  draft: { background: '#FFF8E1', color: '#B8860B', borderColor: '#F0E1A6' },
  active: { background: '#F0FFF4', color: '#2F855A', borderColor: '#C6F6D5' },
  paused: { background: '#EDEDED', color: '#666', borderColor: '#DADADA' },
  completed: { background: '#EDEDED', color: '#666', borderColor: '#DADADA' },
};

// Explicit format (not toLocaleString(), which varies by browser locale) so
// every viewer sees the same layout, matching the dialog's picker.
function formatDateTime(value) {
  if (!value) return '—';
  return moment(value).format('DD/MM/YYYY, h:mm A');
}

// Dates use the viewer's own browser timezone, so the tooltip's UTC
// equivalent is the one reference point that's the same for everyone.
function formatUTCRange(startsAt, endsAt) {
  if (!startsAt || !endsAt) return null;
  const fmt = (value) => moment(value).utc().format('MMM D, YYYY h:mm A');
  return `UTC: ${fmt(startsAt)} → ${fmt(endsAt)}`;
}

const RECURRENCE_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function recurrenceDayLabel(uniqueDays) {
  const isWeekdays = uniqueDays.length === 5 && [1, 2, 3, 4, 5].every((day) => uniqueDays.includes(day));
  const isWeekend = uniqueDays.length === 2 && [0, 6].every((day) => uniqueDays.includes(day));
  if (isWeekdays) return 'Weekdays';
  if (isWeekend) return 'Weekend';
  if (uniqueDays.length === 7) return 'Every day';
  return uniqueDays.map((day) => RECURRENCE_DAY_LABELS[day]).join(', ');
}

// Decodes stored UTC "minute of week" ranges (see NewCampaignDialog.js) into
// the days/hours a viewer would recognize, in their own local time.
function formatRecurrencePattern(campaign) {
  const ranges = campaign.meta?.recurrence?.ranges;
  if (!ranges || ranges.length === 0) return null;

  const days = [];
  let startLabel = '';
  let endLabel = '';
  let fullDay = false;

  ranges.forEach((range) => {
    const startDay = Math.floor(range.start_minute_of_week / 1440);
    const startRemainder = range.start_minute_of_week % 1440;
    const startUtcMoment = moment.utc().day(startDay)
      .hour(Math.floor(startRemainder / 60)).minute(startRemainder % 60).second(0);
    const startLocalMoment = startUtcMoment.clone().local();

    let durationMinutes = range.end_minute_of_week - range.start_minute_of_week;
    if (durationMinutes < 0) durationMinutes += 10080;
    fullDay = durationMinutes === 1440;
    const endLocalMoment = startUtcMoment.clone().local().add(durationMinutes, 'minutes');

    days.push(startLocalMoment.day());
    startLabel = startLocalMoment.format('h:mm A');
    endLabel = endLocalMoment.format('h:mm A');
  });

  const dayLabel = recurrenceDayLabel([...new Set(days)].sort());
  return fullDay ? `${dayLabel} · All day` : `${dayLabel} at ${startLabel} – ${endLabel}`;
}

// Same pattern, kept in UTC rather than decoded to local time - shown in the
// tooltip as the one reference point that's the same for every viewer,
// regardless of their own timezone (same reasoning as formatUTCRange above).
function formatRecurrencePatternUTC(campaign) {
  const ranges = campaign.meta?.recurrence?.ranges;
  if (!ranges || ranges.length === 0) return null;

  const days = [];
  let startLabel = '';
  let endLabel = '';
  let fullDay = false;

  ranges.forEach((range) => {
    const startDay = Math.floor(range.start_minute_of_week / 1440);
    const startRemainder = range.start_minute_of_week % 1440;
    const startUtcMoment = moment.utc().day(startDay)
      .hour(Math.floor(startRemainder / 60)).minute(startRemainder % 60).second(0);

    let durationMinutes = range.end_minute_of_week - range.start_minute_of_week;
    if (durationMinutes < 0) durationMinutes += 10080;
    fullDay = durationMinutes === 1440;
    const endUtcMoment = startUtcMoment.clone().add(durationMinutes, 'minutes');

    days.push(startUtcMoment.day());
    startLabel = startUtcMoment.format('h:mm A');
    endLabel = endUtcMoment.format('h:mm A');
  });

  const dayLabel = recurrenceDayLabel([...new Set(days)].sort());
  return fullDay ? `UTC: ${dayLabel} · All day` : `UTC: ${dayLabel} at ${startLabel} – ${endLabel}`;
}

// Unlike a real name (short by nature, chosen by the user), the title
// fallback is raw popup content (a headline/question) which can run to a
// full sentence or more - too long for this pill-shaped link.
function truncate(text, maxLength = 40) {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

function Campaigns({ site }) {
  const [campaigns, setCampaigns] = useState([]);
  // Only the very first load shows the full-page spinner. Switching tabs (or
  // any later refetch) must not unmount the header/tabs/create button - it
  // should just refresh the list in place, tracked separately via
  // `refreshing` so it doesn't feel like a page reload.
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const API = useAPI();
  const { enqueueSnackbar } = useSnackbar();

  const fetchCampaigns = useCallback(async () => {
    if (hasLoadedOnce.current) {
      setRefreshing(true);
    }
    // No server-side status filter - grouping into Upcoming/Active/Stopped
    // happens client-side below, since a single tab maps to multiple statuses.
    const [ok, data] = await API.campaigns();
    setCampaigns(ok && data?.campaigns ? data.campaigns : []);
    hasLoadedOnce.current = true;
    setInitialLoading(false);
    setRefreshing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleSaved = () => {
    fetchCampaigns();
  };

  const togglePause = async (campaign) => {
    const starting = campaign.status !== 'active';
    const [ok, response] = starting ? await API.activateCampaign(campaign.id) : await API.pauseCampaign(campaign.id);

    if (ok) {
      setCampaigns((prev) => prev.map((c) => (c.id === campaign.id ? response.campaign : c)));
      try {
        window.analytics?.track(starting ? 'Campaign Activated' : 'Campaign Paused', {
          campaign_id: campaign.id,
          goal_type: campaign.goal_type,
          site_id: site?.id,
        });
      } catch (_) {}
    } else {
      enqueueSnackbar(response?.message || 'Could not update campaign', { variant: 'error' });
    }
  };

  const deleteCampaign = async (campaign) => {
    const [ok, response] = await API.deleteCampaign(campaign.id);
    if (ok) {
      setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
      try {
        window.analytics?.track('Campaign Deleted', {
          campaign_id: campaign.id,
          goal_type: campaign.goal_type,
          status: campaign.status,
          site_id: site?.id,
        });
      } catch (_) {}
    } else {
      enqueueSnackbar(response?.message || 'Could not delete campaign', { variant: 'error' });
    }
  };

  if (initialLoading) return <AppLoader visible={true} />;

  const visibleCampaigns = campaigns
    .filter((c) => tab === 'all' || GROUPS[tab].includes(c.status))
    .filter((c) => c.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ backgroundColor: '#F2F1F1', minHeight: '100vh', padding: '0px' }}>
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div>
          <h1 style={{ color: '#23262A', fontWeight: 'bold', fontSize: '28px', margin: 0, lineHeight: '1.2' }}>
            Campaigns
          </h1>
          <p style={{ color: '#777', fontSize: '15px', margin: '8px 0 0 0', maxWidth: '500px' }}>
            Group popups under a schedule that activates and deactivates them automatically.
          </p>
        </div>
        <NewCampaignDialog
          onSaved={handleSaved}
          button={(handleClickOpen) => (
            // Same Button component/props as Widgets.js's "+ Create New Widget"
            // (variant="contained", default "secondary" color) for UI consistency
            // across the two pages - was previously a one-off orange <button>.
            <Button
              variant="contained"
              onClick={handleClickOpen}
              label="+ Create Campaign"
              style={{
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              }}
            />
          )}
        />
      </div>

      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto 20px auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff',
          padding: '10px 20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          border: '1px solid #E5E7EB',
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, val) => setTab(val)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ '& .MuiTab-root': { fontSize: '14px', fontWeight: 'bold', minWidth: '80px' } }}
        >
          <Tab value="all" label="All" />
          <Tab value="upcoming" label="Upcoming" />
          <Tab value="active" label="Active" />
          <Tab value="stopped" label="Stopped" />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: '250px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div style={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 0.15s ease', maxWidth: '1200px', margin: '0 auto' }}>
      {visibleCampaigns.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '80px 40px',
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 15px rgba(0,0,0,0.05)',
            border: '1px solid #e0e0e0',
            marginTop: '20px',
          }}
        >
          <div style={{ fontSize: '50px', marginBottom: '20px' }}>{search ? '🔍' : '📣'}</div>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', color: '#23262A', fontWeight: 'bold' }}>
            {search
              ? 'No campaigns match your search'
              : campaigns.length === 0
              ? "You don't have any campaigns yet"
              : `No ${tab} campaigns`}
          </h3>
          <p style={{ margin: 0, color: '#777', fontSize: '16px' }}>
            {search
              ? 'Try a different search term or check your filters.'
              : 'Create a campaign to group popups under one schedule that turns them off automatically.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {visibleCampaigns.map((campaign) => {
            const canRestart = campaign.status !== 'completed';
            return (
              <div
                key={campaign.id}
                style={{
                  padding: '25px',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  borderRadius: '10px',
                  border: '1px solid #efefef',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '18px', color: '#23262A' }}>{campaign.name}</strong>
                    <div style={{ marginTop: '5px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontWeight: 'bold',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          border: '1px solid',
                          ...(STATUS_COLORS[campaign.status] || STATUS_COLORS.draft),
                        }}
                      >
                        {STATUS_LABELS[campaign.status] || campaign.status}
                      </span>
                      {campaign.goal_type === 'recurring' ? (
                        <Tooltip title={formatRecurrencePatternUTC(campaign) || ''}>
                          <span style={{ fontSize: '13px', color: '#888', cursor: 'help' }}>
                            {formatRecurrencePattern(campaign) || 'No schedule set'}
                            {' · Runs from '}{formatDateTime(campaign.starts_at)}
                            {campaign.ends_at ? ` until ${formatDateTime(campaign.ends_at)}` : ''}
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={formatUTCRange(campaign.starts_at, campaign.ends_at) || ''}>
                          <span style={{ fontSize: '13px', color: '#888', cursor: 'help' }}>
                            {formatDateTime(campaign.starts_at)} → {formatDateTime(campaign.ends_at)}
                          </span>
                        </Tooltip>
                      )}
                      <span style={{ fontSize: '13px', color: '#888' }}>
                        {campaign.popup_count} {campaign.popup_count === 1 ? 'popup' : 'popups'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <Tooltip
                      title={
                        !canRestart
                          ? "This campaign finished on its own and can't be restarted"
                          : campaign.status === 'active'
                          ? 'Stop campaign'
                          : 'Start campaign'
                      }
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          checked={campaign.status === 'active'}
                          onChange={() => togglePause(campaign)}
                          disabled={!canRestart}
                          color="primary"
                          size="small"
                        />
                      </div>
                    </Tooltip>

                    <NewCampaignDialog
                      campaign={campaign}
                      onSaved={handleSaved}
                      button={(handleClickOpen) => (
                        <Tooltip title="Edit campaign">
                          <IconButton onClick={handleClickOpen} size="small" style={{ color: '#ED5A29', background: '#FFF0EB' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />

                    <ConfirmationDialog
                      onConfirm={() => deleteCampaign(campaign)}
                      component={(props) => (
                        <Tooltip title="Delete campaign">
                          <IconButton {...props} size="small" style={{ color: '#d32f2f', background: '#FFEBEE' }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    >
                      {/* Its popups are already off once a campaign is Stopped (paused or
                          completed) - only mention pausing when deleting actually turns
                          something off, i.e. an Active or Upcoming campaign. "Paused" (not
                          "deactivated") since this uses the same reversible paused_at as the
                          regular Manage-page pause toggle - "deactivated" implies the
                          unrelated, more severe whole-site suspension mechanism. */}
                      {GROUPS.stopped.includes(campaign.status)
                        ? "Delete this campaign? This can't be undone."
                        : "Delete this campaign? Its popups will be paused and this can't be undone."}
                    </ConfirmationDialog>
                  </div>
                </div>

                <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #f0f0f0' }}>
                  {campaign.popups?.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {campaign.popups.map((popup) => (
                        <Link
                          key={popup.id}
                          to={editorURL(site, `/${popup.id}/design`)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            color: '#476684',
                            background: '#F0F4F8',
                            border: '1px solid #D1D9E0',
                            borderRadius: '20px',
                            padding: '4px 12px',
                            textDecoration: 'none',
                          }}
                        >
                          {/* Same fallback as the Manage/popups list (PopupItem.js): a popup's
                              actual headline/question text when it has no name set, instead of
                              a generic "Untitled X" label. Truncated here (unlike PopupItem.js)
                              since this pill has no room for a full sentence of raw content. */}
                          {popup.name || truncate(popup.title) || `Untitled ${popup.type || 'Popup'}`}
                          {!popup.active && <span style={{ color: '#999' }}>(off)</span>}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '13px', color: '#aaa' }}>
                      No popups attached yet - open a popup's editor and set its Campaign field.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

export default Campaigns;
